import { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Controller } from "react-hook-form";
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    Row,
    Tag,
    Typography,
} from "antd";
import {
    ArrowLeftOutlined,
    CheckOutlined,
    DeleteOutlined,
    GiftOutlined,
    InboxOutlined,
    PictureOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import Dragger from "antd/es/upload/Dragger";

import { IAwardType } from "../../../../interfaces";
import useAwardTypeForm from "./useAwardTypeForm";
import PageHeader from "../../../../components/PageHeader";
import { ThemeContext } from "../../../../contexts/ThemeContext";

const { Text } = Typography;

interface IProps {
    action: "Adicionar" | "Editar";
    awardType?: IAwardType;
    onSubmit: (data: FormData) => Promise<void>;
}

export default function AwardTypeForm({ action, awardType, onSubmit }: IProps) {
    const {
        handleSubmitForm,
        handleSubmit,
        isSubmiting,
        errors,
        control,
        dummyRequest,
        handleChangeDefaultImage,
        handleRemoveImage,
        defaultImageUrl,
    } = useAwardTypeForm(onSubmit, awardType);

    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const navigate = useNavigate();

    const isEdit = action === "Editar";

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}`,
                    padding: "10px 16px",
                    borderRadius: 8,
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/configuracoes/tipos-de-premios">Tipos de Prêmios</Link>,
                        },
                        {
                            title: isEdit ? `Editar Tipo de Prêmio #${awardType?.id ?? "--"}` : "Novo Tipo de Prêmio",
                        },
                    ]}
                />
            </div>

            <PageHeader
                icon={<GiftOutlined />}
                iconBg="linear-gradient(135deg, #faad14 0%, #d48806 100%)"
                title={isEdit ? `Editar Tipo de Prêmio: ${awardType?.name || ""}` : "Novo Tipo de Prêmio"}
                tag={isEdit && awardType?.id ? <Tag color="gold">ID #{awardType.id}</Tag> : undefined}
                subtitle={
                    isEdit
                        ? "Atualize o nome e a imagem padrão vinculada a este tipo de prêmio para sorteios"
                        : "Cadastre uma nova categoria de prêmio para utilização nos sorteios do município"
                }
                actions={
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate("/configuracoes/tipos-de-premios")}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 500,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Voltar para Tipos de Prêmios
                    </Button>
                }
            />

            <Card
                style={{
                    borderRadius: 12,
                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                    background: isDark ? "#141414" : "#ffffff",
                    boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.03)",
                }}
                bodyStyle={{ padding: "20px 24px" }}
            >
                <Form
                    onFinish={handleSubmit(handleSubmitForm)}
                    autoComplete="off"
                    layout="vertical"
                >
                    <div style={{ marginBottom: 20 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                            }}
                        >
                            <GiftOutlined style={{ color: "#faad14", fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                    Identificação do Tipo de Prêmio
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Nome da categoria de prêmio (Ex: Moto 0km, TV 50", Vale-Compras, Bicicleta)
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={14}>
                                <Form.Item
                                    label={<strong>Nome do Tipo de Prêmio</strong>}
                                    required
                                    validateStatus={errors.name ? "error" : undefined}
                                    help={errors.name?.message}
                                >
                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{
                                            required: "Por favor, digite o nome do tipo de prêmio!",
                                            minLength: {
                                                value: 2,
                                                message: "O nome deve ter pelo menos 2 caracteres.",
                                            },
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Ex: Automóvel 0km, Smartphone, Cesta Básica"
                                                size="large"
                                                style={{ borderRadius: 8 }}
                                                prefix={<GiftOutlined style={{ color: "#faad14" }} />}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <div style={{ marginBottom: 24 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                            }}
                        >
                            <PictureOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                    Imagem Padrão do Prêmio
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Foto ou ilustração exibida aos cidadãos no aplicativo e no portal da transparência
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={14}>
                                <Form.Item
                                    label={<strong>Arquivo de Imagem</strong>}
                                    required={!awardType}
                                    validateStatus={errors.default_image ? "error" : undefined}
                                    help={errors.default_image?.message}
                                >
                                    <Controller
                                        name="default_image"
                                        control={control}
                                        rules={{
                                            required: {
                                                value: !awardType && !defaultImageUrl,
                                                message: "Por favor, insira a imagem padrão do tipo de prêmio!",
                                            },
                                        }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <div>
                                                {defaultImageUrl && typeof defaultImageUrl === "string" ? (
                                                    <div
                                                        style={{
                                                            border: `1px solid ${isDark ? "#303030" : "#e2e8f0"}`,
                                                            borderRadius: 10,
                                                            padding: 16,
                                                            background: isDark ? "#1f1f1f" : "#f8fafc",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            flexWrap: "wrap",
                                                            gap: 16,
                                                        }}
                                                    >
                                                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                                            <img
                                                                src={defaultImageUrl}
                                                                alt="Imagem padrão do tipo de prêmio"
                                                                style={{
                                                                    width: 80,
                                                                    height: 80,
                                                                    objectFit: "contain",
                                                                    borderRadius: 8,
                                                                    background: isDark ? "#141414" : "#ffffff",
                                                                    border: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                                                                    padding: 4,
                                                                }}
                                                            />
                                                            <div>
                                                                <div style={{ fontWeight: 600, fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                                                                    Imagem carregada
                                                                </div>
                                                                <div style={{ fontSize: 11.5, color: isDark ? "#8c8c8c" : "#64748b", marginTop: 2 }}>
                                                                    Formatos aceitos: PNG, JPG, JPEG, WEBP
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                            <Dragger
                                                                {...field}
                                                                showUploadList={false}
                                                                customRequest={dummyRequest}
                                                                onChange={(e: UploadChangeParam<UploadFile>) =>
                                                                    handleChangeDefaultImage({ onChangeHookForm: onChange, file: e.file })
                                                                }
                                                                accept="image/png, image/jpg, image/jpeg, image/webp"
                                                                style={{ padding: 0, border: "none", background: "transparent" }}
                                                            >
                                                                <Button icon={<UploadOutlined />} style={{ borderRadius: 6, fontWeight: 500 }}>
                                                                    Trocar Imagem
                                                                </Button>
                                                            </Dragger>
                                                            <Button
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                onClick={handleRemoveImage}
                                                                style={{ borderRadius: 6 }}
                                                            >
                                                                Remover
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Dragger
                                                        {...field}
                                                        showUploadList={false}
                                                        customRequest={dummyRequest}
                                                        onChange={(e: UploadChangeParam<UploadFile>) =>
                                                            handleChangeDefaultImage({ onChangeHookForm: onChange, file: e.file })
                                                        }
                                                        accept="image/png, image/jpg, image/jpeg, image/webp"
                                                        style={{
                                                            borderRadius: 10,
                                                            border: `2px dashed ${errors.default_image
                                                                ? "#ff4d4f"
                                                                : isDark
                                                                    ? "#303030"
                                                                    : "#d9d9d9"
                                                                }`,
                                                            background: isDark ? "#1f1f1f" : "#fafafa",
                                                            padding: "24px 16px",
                                                        }}
                                                    >
                                                        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                                            <InboxOutlined style={{ color: "#faad14", fontSize: 36 }} />
                                                        </p>
                                                        <p
                                                            style={{
                                                                fontWeight: 600,
                                                                fontSize: 13.5,
                                                                color: isDark ? "#f0f0f0" : "#1e293b",
                                                                marginBottom: 4,
                                                            }}
                                                        >
                                                            Clique ou arraste a imagem do prêmio aqui
                                                        </p>
                                                        <p style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b", margin: 0 }}>
                                                            Formatos aceitos: PNG, JPG, JPEG e WEBP (máx. 5MB)
                                                        </p>
                                                    </Dragger>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <Divider style={{ margin: "20px 0 16px 0" }} />

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
                        <Button
                            onClick={() => navigate("/configuracoes/tipos-de-premios")}
                            disabled={isSubmiting}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 500,
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<CheckOutlined />}
                            loading={isSubmiting}
                            disabled={isSubmiting}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 600,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            {isEdit ? "Salvar Alterações" : "Cadastrar Tipo de Prêmio"}
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
}
